// Kompass-Rose mit Dreifach-Rolle (User-Wunsch Batch 7):
//   1. ANZEIGE: 4 kleine Pfeile (N/E/S/W), der rote zeigt Norden. Rotiert
//      gegenläufig zum Map-Bearing, damit "Norden" auf dem Bildschirm IMMER
//      die echte Nordrichtung ist — egal ob die Karte starr-genorded ist
//      oder sich per Handy-Kompass dreht.
//   2. TOGGLE-BUTTON: Tap zyklt durch 3 Modi in dieser Reihenfolge:
//        north   → Karte starr genorded (bearing=0). Rose: rotes N + graue
//                  Nebenpfeile. Hintergrund: weiß. Rotation-Handler IST aktiv
//                  (s.u.), nur wechseln wir bei 2-Finger-Rotate automatisch
//                  in den free-Modus — statt die Geste zu blockieren.
//        heading → Karte dreht sich mit Handy-Kompass. 2-Finger-Rotate hart
//                  blockiert (sonst kämpft User mit dem Compass-Listener).
//                  Rose: wie north (rot-N). Hintergrund: teal.
//        free    → User darf mit 2 Fingern frei rotieren. App erzwingt kein
//                  Bearing. Rose: kleiner grauer Stern + Rotations-Arc drumrum
//                  als Hinweis "hier kannst du drehen". Hintergrund: weiß.
//   3. PROGRAMMATIC: setMode() von außen — MapPage schaltet beim Ride-Start
//      auf 'heading' (User-Wunsch aus früheren Batches).
//
// User-Wunsch Batch 7:
// (a) "ich kann die karte mit 2 fingern nicht frei rotieren, manchmal möchte
//     ich das als user um zb einen ort genau unten zu sehen". Lösung: neuer
//     'free'-Modus.
// (b) "north-heading-free-north und wenn ich in north modus versuche, die
//     karte mit 2 fingern zu drehen, soll automatisch in den free gesprungen
//     werden". Lösung: in north-Modus bleibt die Touch-Rotation ENABLED, ein
//     rotatestart-Listener fängt die Geste ab und wechselt sofort in free.
//     So muss der User nicht erst den Kompass-Button tappen.
//
// iOS 13+ verlangt explizite Permission via DeviceOrientationEvent.requestPermission().
// Das muss im User-Gesture-Kontext passieren — also direkt im click-Handler,
// await-gechained ist ok, Safari akzeptiert das.

import type { ControlPosition, IControl, Map as MLMap, MapLibreEvent } from 'maplibre-gl';
import { deviceHeadingBus } from './deviceHeadingBus';
import { positionBus } from '../positionBus';
import { angleDelta } from './angleMath';

export type CompassMode = 'north' | 'free' | 'heading';

// --- Heading-Dämpfung im heading-Modus ---------------------------------------
//
// Vor Wave 14ax drehte sich die Karte im Heading-Modus auf jedes Kompass-
// Event (deviceorientation feuert ~60 Hz auf iOS). Ein 1°-Mini-Filter war da,
// aber zwei Probleme:
//   1. Naiver Math.abs(a-b)-Vergleich wrappt nicht um 0°/360° — nahe Norden
//      verbrät sich der Filter, weil 359°↔1° als "358° Sprung" erscheint.
//      Jetzt: korrekter angleDelta() aus ./angleMath.
//   2. 1° ist zu klein — Handlebar-Wobble ist typisch 1-2° und triggert
//      sichtbare Rotationen. 2.5° schluckt den Wobble, lässt echte Kurven
//      (die immer deutlich grösser sind) sofort durch.
const HEADING_DEAD_ZONE_DEG = 2.5;

// Throttle auf ~15 Hz. Das cappt die teuren MapLibre setBearing-Calls auf
// max ~15/sec, was für Kurven immer noch butterweich wirkt, aber die Akku-
// hungrigen 60-Hz-Updates kappt. 66.7ms Throttle-Lag ist unter der Wahr-
// nehmungsschwelle.
const HEADING_MIN_INTERVAL_MS = 1000 / 15;

// --- GPS-vs-Kompass-Hysterese (Map-Bearing-Quelle) ---------------------------
//
// Im heading-Modus dreht sich die Karte standardmässig nach dem Magnet-
// kompass. Der ist aber bei Bewegung am Fahrrad-Lenker extrem nervös
// (E-Bike-Motor, Kabel, Metall-Lenker → alles stört den Magnetometer).
// GPS-Kurs (coords.heading) ist dann die viel ruhigere Quelle: das OS
// liefert ihn erst wenn die Bewegung klar genug ist und hat ihn selbst
// schon gefiltert.
//
// Heuristik mit Hysterese, damit wir nicht am Übergangsbereich zappeln:
//   - ENTER_SPEED (GPS übernimmt): >= 2.5 m/s  (= 9 km/h, klar "Fahrt")
//   - EXIT_SPEED  (zurück zu Kompass): < 1.5 m/s (= 5.4 km/h, klar "langsam")
//   - Dazwischen bleibt der aktuelle Zustand.
//   - ACCURACY    (GPS-Kurs nur wenn Fix gut ist): <= 25 m accuracy.
//
// WICHTIG: bei GPS-Wechsel zurück auf Kompass bleibt der Kompass-Listener
// aktiv (dünn laufender Bus ist billig). Der User kann im Stand also weiter-
// hin das Handy drehen um z.B. einen Berg zu suchen — die Karte reagiert
// sofort. Das war der explizite User-Wunsch.
const GPS_SOURCE_ENTER_SPEED = 2.5;
const GPS_SOURCE_EXIT_SPEED = 1.5;
const GPS_SOURCE_MAX_ACCURACY_M = 25;

// BUILD-VERSION-MARKER (Wave 14aq): Farbe kommt per Vite-Env-Var, gesetzt
// vom Deploy-Script via `./scripts/deploy-staging.sh --color blue`. So sieht
// der User beim ersten Blick auf die Rose, ob die NEUE Build-Version geladen
// wurde (frischer Deploy = frische Farbe).
//
// Vite inlined `import.meta.env.VITE_*` AT BUILD TIME — es gibt keine
// Runtime-Kosten. Wenn der Deploy ohne --color läuft, fällt es auf das
// Default-Magenta zurück (wie in den Waves davor).
//
// Gültige Werte: jeder CSS-Color-String (Hex, Named, rgb()). Das Mapping
// "blau → #3b82f6" o.ä. passiert im Deploy-Script; hier kommt bereits der
// fertige CSS-Wert an.
const BUILD_MARKER_COLOR = import.meta.env.VITE_BUILD_MARKER_COLOR || '#ff00ff';

// SVG-Content pro Modus. Wird in _refreshUi() in den Rose-Container geschrieben.
//   NORTH/HEADING: der klassische 4-Pfeil-Stern mit rotem N. Rotiert counter-
//     bearing, sodass der rote Pfeil IMMER nach echtem Norden zeigt.
//   FREE: kompakterer grauer Stern (alle 4 Pfeile gleich, kein rotes N) +
//     ein Rotations-Arc außen rechts mit Arrowhead, als visueller Hinweis
//     "diese Map lässt sich jetzt drehen". Bewusst KEINE rote Farbe — Rot
//     ist für den festen-Norden-Zustand reserviert. Im free-Modus verzichten
//     wir auf die Counter-Rotation (s. _update()), damit der Arc ruhig steht.
// User-Feedback 14f: "kompassrose immer nicht mittig". Ursache war NICHT
// ein Flex/Container-Alignment-Bug, sondern der viewBox selbst: die Arme
// reichten nur ±16 in einen ±20-Viewport, d.h. das sichtbare Motiv füllte
// nur 80% des SVGs. Visuell wirkt das wie "kleiner Stern in großer Box"
// = optisch off-center im 40px-Button neben der satt gefüllten Locate-
// Icon oben. Fix: viewBox auf die tatsächlichen Motiv-Bounds getrimmt
// (`-17 -17 34 34`, 1 Unit Padding rundum), sodass bei SVG-width 32px
// die Arme ~30px ausfüllen. Motiv-Geometrie bleibt symmetrisch um (0,0).
//
// Free-Mode: vorher war der Rotations-Arc einseitig rechts — das schob
// das visuelle Schwergewicht nach rechts, selbst bei geometrischer
// Symmetrie. Ersetzt durch ZWEI symmetrische Arcs links+rechts, sodass
// das Motiv auch wirklich um (0,0) balanciert ist.
// Wave 14dh: Sekundär-Arme nutzen currentColor statt hardcoded #e2e8f0
// damit Light-Mode-CSS sie zum slate-700 schalten kann (auf weissem
// Pill-Hintergrund sonst unsichtbar). Roter Nord-Arm bleibt #ef4444
// — soll auch im Light-Mode rot stehen.
const NORTH_ROSE_SVG = `<svg viewBox="-17 -17 34 34" width="32" height="32" aria-hidden="true" style="display:block;margin:0;padding:0;color:currentColor;"><polygon points="0,-16 4,-4 -4,-4" fill="#ef4444"/><polygon points="16,0 4,4 4,-4" fill="currentColor"/><polygon points="0,16 -4,4 4,4" fill="currentColor"/><polygon points="-16,0 -4,-4 -4,4" fill="currentColor"/><circle cx="0" cy="0" r="1.8" fill="#0f172a"/></svg>`;

const FREE_ROSE_SVG = `<svg viewBox="-17 -17 34 34" width="32" height="32" aria-hidden="true" style="display:block;margin:0;padding:0;color:currentColor;"><polygon points="0,-10 3,-2.5 -3,-2.5" fill="currentColor"/><polygon points="10,0 2.5,3 2.5,-3" fill="currentColor"/><polygon points="0,10 -3,2.5 3,2.5" fill="currentColor"/><polygon points="-10,0 -2.5,-3 -2.5,3" fill="currentColor"/><circle cx="0" cy="0" r="1.4" fill="#64748b"/><path d="M 13.5,-5 A 14.4 14.4 0 0 1 13.5 5" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/><path d="M -13.5,-5 A 14.4 14.4 0 0 0 -13.5 5" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>`;

function titleForMode(m: CompassMode): string {
  if (m === 'north') return 'Compass: locked to north · tap to follow phone heading · drag to rotate freely';
  if (m === 'heading') return 'Compass: follows phone heading · tap to lock north · drag to rotate freely';
  return 'Compass: free rotation · tap to lock north';
}

export class CompassRose implements IControl {
  private _map?: MLMap;
  private _container!: HTMLDivElement;
  private _button!: HTMLButtonElement;
  private _rose!: HTMLDivElement;
  private _mode: CompassMode = 'north';
  // Wave 14am: statt eigenem window-Listener nehmen wir den shared
  // deviceHeadingBus — das teilt Listener & Permission mit LocateControl.
  // _unsubHeading ist die Unsubscribe-Funktion des Bus-Abonnements; null
  // solange wir nicht im heading-Mode sind.
  private _unsubHeading: (() => void) | null = null;
  // Wave 14ax: positionBus-Abo parallel zum deviceHeadingBus. Wenn der User
  // schnell genug fährt (> ENTER_SPEED, gute GPS-Accuracy), füttern wir die
  // Map-Bearing aus coords.heading (Course over Ground) — viel ruhiger als
  // der Magnetkompass am E-Bike-Lenker. Bei Stand oder schlechter Accuracy
  // fallen wir auf den Kompass zurück.
  private _unsubPosition: (() => void) | null = null;
  // Ob aktuell der GPS-Kurs die Map-Bearing treibt (true) oder der Kompass
  // (false). Wechselt mit Hysterese (ENTER_SPEED vs EXIT_SPEED).
  private _gpsActive = false;
  // Letztes an map.setBearing() weitergeleitetes Heading — Referenz für die
  // Dead-Zone-Prüfung. NICHT "das letzte empfangene Heading", sondern "das
  // letzte, das wir auch wirklich angewendet haben". Dadurch akkumulieren
  // sich Sub-Dead-Zone-Wiggles nicht.
  private _lastAppliedBearing: number | null = null;
  // Zeitstempel (performance.now) der letzten angewendeten Bearing. Wird für
  // das 15-Hz-Throttle konsultiert.
  private _lastApplyTime = 0;
  // Wave 14bh: Mode-Change-Pub-Sub. Consumer wie MapPage wollen auf jeden
  // Mode-Wechsel reagieren (z.B. Hillshade aus, wenn heading aktiv wird).
  // Ein einfaches Set<Listener> reicht — es gibt pro Session selten mehr
  // als 1-2 Abonnenten, und die Listener werden bei Mode-Wechsel synchron
  // vor der easeTo-Animation gefeuert.
  private readonly _modeListeners = new Set<(m: CompassMode) => void>();

  onAdd(map: MLMap): HTMLElement {
    this._map = map;
    this._container = document.createElement('div');
    // `adt-ctrl` + `adt-ctrl-btn` teilen das Turbo-Dark-Pill-Design mit
    // dem LocateControl (siehe styles/index.css).
    this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group adt-ctrl';

    this._button = document.createElement('button');
    this._button.type = 'button';
    this._button.className = 'adt-ctrl-btn adt-compass-btn';
    // BUILD-VERSION-MARKER (User-Wunsch ab Wave 14ao): ein farbiger Inset-
    // Border um die Kompass-Rose signalisiert "du hast die neueste gebaute
    // Version dieser PWA". Bei jedem Deploy wird per `--color X` Flag des
    // Deploy-Scripts eine andere Farbe gesetzt — dann sieht der User beim
    // ersten Blick auf staging oder prod ob der Service-Worker-Cache die
    // frische Version geladen hat.
    //
    // Farbe kommt aus VITE_BUILD_MARKER_COLOR (siehe Top-of-File); Default
    // ist magenta #ff00ff wenn das Env-Var nicht gesetzt ist.
    //
    // WICHTIG: setProperty mit 'important', weil styles/index.css Zeile ~159
    // `.adt-ctrl.maplibregl-ctrl-group button { box-shadow: none !important; }`
    // hat → würde inline-Style ohne !important killen. Inline + !important
    // beats stylesheet + !important per CSS-Spezifität.
    this._button.style.setProperty(
      'box-shadow',
      `inset 0 0 0 3px ${BUILD_MARKER_COLOR}`,
      'important',
    );
    this._button.onclick = () => void this._toggle();

    this._rose = document.createElement('div');
    this._rose.className = 'adt-compass-rose';
    // Kurze CSS-Transition glättet die Rotation bei jedem bearing-Update,
    // sonst wirkt's ruckelig bei Heading-Modus.
    this._rose.style.transition = 'transform 120ms linear';
    this._rose.style.willChange = 'transform';

    this._button.appendChild(this._rose);
    this._container.appendChild(this._button);

    // Startzustand anwenden: rotation-lock für 'north', SVG rendern, Title setzen.
    this._applyRotationLock();
    this._refreshUi();
    this._update();

    // Wave 14bo (Energiespar-Tipp #2): _update liest ausschließlich
    // map.getBearing(). Bearing ändert sich NUR bei Rotation, und MapLibre
    // feuert auch bei flyTo({bearing}) / easeTo({bearing}) brav 'rotate'.
    // Das vorher zusätzlich registrierte 'move' feuerte bei jedem Pan/Zoom
    // und triggerte 60× pro Sekunde ein Re-Apply derselben Transform — auf
    // dem Moto-Ride bei dauerndem flyTo-Following pure CPU-Verschwendung.
    map.on('rotate', this._update);
    // Auto-Switch von 'north' in 'free' bei 2-Finger-Rotation-Versuch. Der
    // Listener bleibt immer registriert; er filtert selbst nach Modus.
    map.on('rotatestart', this._onRotateStart);
    return this._container;
  }

  onRemove(): void {
    this._disableOrientation();
    const map = this._map;
    if (map) {
      map.off('rotate', this._update);
      map.off('rotatestart', this._onRotateStart);
    }
    this._container.parentNode?.removeChild(this._container);
    this._map = undefined;
    this._modeListeners.clear();
  }

  getDefaultPosition(): ControlPosition {
    return 'top-right';
  }

  /**
   * Wave 14ca-fix5: Tap-Logik vereinfacht (User-Wunsch).
   * Tap zyklt jetzt nur noch BINÄR zwischen north ↔ heading. Der
   * 'free'-Mode wird vom Tap NIE direkt erreicht — er erscheint nur
   * automatisch, sobald der User versucht, die Karte mit 2 Fingern zu
   * drehen (egal ob aus north oder heading, s. _onRotateStart). Aus
   * 'free' bringt jeder Tap zurück nach 'north' — direkte Brücke zurück
   * zum Nord-Modus, ohne erst durch heading zu müssen.
   *
   * Resultierende Cycle-Tabelle:
   *   north   --tap--> heading
   *   heading --tap--> north
   *   free    --tap--> north
   *   north   --drag--> free
   *   heading --drag--> free
   *
   * `isUserInitiated: true` sagt setMode(), dass bei Permission-Deny ein
   * sichtbares alert() geworfen werden soll — der Tap darf NIE silent sein,
   * sonst wirkt die Rose "kaputt".
   */
  private async _toggle(): Promise<void> {
    const next: CompassMode =
      this._mode === 'north' ? 'heading' : 'north';
    await this.setMode(next, true);
  }

  /**
   * Öffentliche API — setzt den Modus von außen.
   *
   * Wird z.B. vom Ride-Button in MapPage gerufen: beim Enter von Ride-Mode soll
   * die Rose automatisch in 'heading' wechseln (User-Wunsch). Der Aufruf erfolgt
   * synchron aus dem click-Handler der Ride-Button, daher ist der User-Gesture-
   * Kontext für die iOS-Permission erhalten.
   *
   * `isUserInitiated` (default false):
   *   - false → Permission-Deny ist silent (console.warn, kein alert). Gedacht
   *     für programmatische Calls wie Ride-Start: wenn der User die Permission
   *     schon früher verweigert hat, wollen wir ihn nicht bei jedem Ride-Tap
   *     mit einem Popup nerven.
   *   - true  → Permission-Deny zeigt einen sichtbaren alert mit iOS-Reset-
   *     Anleitung. Gedacht für den direkten Kompass-Rose-Tap: wenn dort
   *     "nichts passiert", ärgert sich der User zu Recht (Batch 7 feedback:
   *     "klick auf die rose: nichts passiert").
   *
   * Idempotent: wenn der gewünschte Modus schon aktiv ist, passiert nichts.
   * Gibt `true` zurück bei Erfolg, `false` wenn Permission abgelehnt wurde.
   */
  public async setMode(target: CompassMode, isUserInitiated = false): Promise<boolean> {
    const map = this._map;
    if (!map) return false;
    if (this._mode === target) return true;

    // Compass-Listener bei Moduswechsel IMMER erstmal aus — nur 'heading'
    // setzt ihn gleich wieder. Verhindert Listener-Leaks wenn man mehrfach
    // zwischen Modi wechselt.
    this._disableOrientation();

    if (target === 'north') {
      this._setModeInternal('north');
      this._applyRotationLock();
      // Wieder genorded einfliegen. Pitch 0 hinterher auch zur Sicherheit.
      map.easeTo({ bearing: 0, pitch: 0, duration: 300 });
      this._refreshUi();
      this._update();
      return true;
    }

    if (target === 'free') {
      this._setModeInternal('free');
      this._applyRotationLock();
      // KEIN easeTo — der User behält das aktuelle Bearing. Er will ja
      // frei weitermachen wo wir stehen.
      this._refreshUi();
      this._update();
      return true;
    }

    // target === 'heading'
    // Wave 14am: Permission und Listener kommen beide über den shared
    // deviceHeadingBus — das hält es synchron mit LocateControl's Dreieck.
    const ok = await deviceHeadingBus.requestPermissionIfNeeded();
    if (!ok) {
      console.warn('[compass] Permission für Device-Orientation abgelehnt', {
        isUserInitiated,
      });
      if (isUserInitiated) {
        // User-initiated Tap → NIE silent. Sonst wirkt die Rose "tot".
        //
        // iOS-Hintergrund: DeviceOrientationEvent.requestPermission() kann pro
        // Site-Session nur EINMAL prompten. Einmal 'denied' → ab dann liefert
        // jeder Call sofort 'denied' ohne neuen Prompt. Reset geht NICHT über
        // iOS-Einstellungen (es gibt keine pro-Site Motion-Access-UI), sondern
        // nur über:
        //   (a) Safari komplett beenden (App-Switcher wegwischen) + neu öffnen,
        //   (b) Bei PWA: App beenden + neu starten (oder sogar löschen und neu
        //       installieren via Add-to-Homescreen).
        //
        // Android/Desktop kommen NIE hier rein — requestPermissionIfNeeded
        // returnt dort sofort true ohne API-Call. Wenn das alert also doch
        // auftaucht, ist der User fast sicher auf iOS.
        alert(
          'Rotating the map with the phone compass needs access to the ' +
            'motion sensors.\n\n' +
            'iOS denied access (or the dialog never appeared). Please ' +
            'fully close Safari (or the app) via the app switcher, reopen ' +
            'it, and on the next tap on the compass rose choose "Allow".',
        );
      }
      return false;
    }
    this._unsubHeading = deviceHeadingBus.subscribe((heading) => {
      this._onHeading(heading);
    });
    // Parallel zum Kompass: positionBus abonnieren, damit wir bei Fahrt
    // auf den ruhigeren GPS-Kurs umschalten können (s. _onPosition für die
    // Hysterese-Kriterien). Initial mit dem aktuellen Fix primen — damit
    // beim Start in voller Fahrt nicht erst auf den nächsten Geolocation-
    // Tick gewartet werden muss.
    this._unsubPosition = positionBus.subscribe((coords) => {
      this._onPosition(coords);
    });
    this._onPosition(positionBus.get());
    this._setModeInternal('heading');
    this._applyRotationLock();
    map.easeTo({ pitch: 0, duration: 300 });
    this._refreshUi();
    this._update();
    return true;
  }

  /** Aktueller Modus — gelesen von außen (z.B. für UI-Zustand). */
  public getMode(): CompassMode {
    return this._mode;
  }

  /**
   * Wave 14bh: Mode-Change-Pub-Sub.
   *
   * Consumer (z.B. MapPage) abonnieren sich hier, wenn sie auf jeden
   * Mode-Wechsel reagieren müssen. Use-Case: Hillshade-Layer-Sichtbarkeit
   * — der DEM-Shader ist teuer bei jeder Bearing-Änderung (viewport-
   * anchor), also blenden wir den Layer im heading-Mode komplett aus.
   *
   * Der Listener wird synchron beim Mode-Wechsel gefeuert (noch vor der
   * easeTo-Kamera-Animation), nicht debounced. Idempotente `setMode()`-
   * Calls feuern nicht (s. early-return in setMode). Rückgabe: Unsubscribe.
   */
  public onModeChange(fn: (m: CompassMode) => void): () => void {
    this._modeListeners.add(fn);
    return () => {
      this._modeListeners.delete(fn);
    };
  }

  /**
   * Intern: Setzt den Mode UND benachrichtigt die Mode-Change-Listener.
   * Immer über diese Methode gehen, damit kein Pfad Listener verpasst.
   */
  private _setModeInternal(next: CompassMode): void {
    if (this._mode === next) return;
    this._mode = next;
    for (const fn of this._modeListeners) {
      try {
        fn(next);
      } catch (err) {
        console.warn('[compass] modeListener threw', err);
      }
    }
  }

  /**
   * Rotation-Handler von MapLibre je Modus aktivieren/deaktivieren.
   *
   * Wave 14ca-fix5: Rotation jetzt in ALLEN Modi an.
   * - free: dragRotate + touchRotate + keyboardRotate alle AN — User dreht
   *   frei.
   * - north: ebenfalls AN. Wir blockieren nicht, sondern fangen die
   *   rotatestart-Geste ab und wechseln in free-Modus (s. _onRotateStart).
   *   So wirkt die App "nachgiebig" statt starr für den User.
   * - heading: jetzt AUCH AN (vorher hart deaktiviert). Der erste
   *   rotatestart deaktiviert via setMode('free') sofort den Compass-
   *   Listener — danach dreht der User ungehindert weiter. Das winzige
   *   Race-Window zwischen Touch-Start und rotatestart-Event ist in
   *   der Praxis irrelevant: der Compass tickt auf max 15 Hz, der
   *   rotatestart-Event feuert innerhalb weniger Millisekunden.
   */
  private _applyRotationLock(): void {
    const map = this._map;
    if (!map) return;
    map.dragRotate.enable();
    map.touchZoomRotate.enableRotation();
    map.keyboard.enableRotation?.();
  }

  /**
   * Feuert, sobald der User eine Rotations-Geste startet. In 'north' UND
   * 'heading' wechseln wir automatisch in 'free' — der User hat klar
   * signalisiert, dass er drehen will. In 'free' bleiben wir.
   *
   * Wave 14ca-fix5: Auto-Switch jetzt auch aus heading. Vorher war
   * dragRotate in heading hart deaktiviert — der User konnte die Karte
   * nicht drehen, während die Kompass-Steuerung lief. Jetzt: Rotation
   * ist erlaubt, der erste rotatestart deaktiviert die Kompass-
   * Steuerung (via setMode('free') → _disableOrientation) und der User
   * dreht ungehindert weiter.
   *
   * Wichtig: setMode('free') ändert internen State + UI + Rotation-
   * Lock + unsubst den Heading-Bus (alles synchron — kein await für
   * 'free'-Target). Die laufende Rotation wird nicht unterbrochen,
   * MapLibre führt die Geste fertig durch.
   *
   * WICHTIGER BUGFIX (User-Wunsch früher): wir filtern auf
   * `e.originalEvent`, denn MapLibre feuert `rotatestart` AUCH bei
   * programmatischen Rotationen (z.B. `easeTo({ bearing: 0 })` in
   * setMode('north')). Ohne Filter würde das Einnorden vom free-Mode
   * aus sofort in free zurückspringen — man sieht dann zwar die Karte
   * wieder nach Norden drehen, aber das Kompass-Icon bleibt auf
   * "free". Bei echten User-Gesten ist `originalEvent` gesetzt
   * (TouchEvent/MouseEvent/KeyboardEvent), bei programmatischen
   * Rotationen fehlt es.
   */
  private _onRotateStart = (e: MapLibreEvent): void => {
    if (!e.originalEvent) return;
    if (this._mode === 'north' || this._mode === 'heading') {
      void this.setMode('free');
    }
  };

  /**
   * SVG-Icon + Button-Hintergrund + aria-label/title je Modus aktualisieren.
   */
  private _refreshUi(): void {
    if (this._rose) {
      this._rose.innerHTML = this._mode === 'free' ? FREE_ROSE_SVG : NORTH_ROSE_SVG;
    }
    if (this._button) {
      // Mode als data-attribute → CSS rendert den aktiven Zustand
      // (styles/index.css: .adt-compass-btn[data-mode="heading"]).
      // Free-Mode bleibt neutral; Heading bekommt den Turbo-Akzent.
      this._button.dataset.mode = this._mode;
      this._button.style.background = '';
      const label = titleForMode(this._mode);
      this._button.setAttribute('aria-label', label);
      this._button.title = label;
    }
  }

  private _disableOrientation(): void {
    if (this._unsubHeading) {
      this._unsubHeading();
      this._unsubHeading = null;
    }
    if (this._unsubPosition) {
      this._unsubPosition();
      this._unsubPosition = null;
    }
    this._gpsActive = false;
    this._lastAppliedBearing = null;
    this._lastApplyTime = 0;
  }

  /**
   * Callback für den deviceHeadingBus — der Kompass spricht.
   *
   * Während der GPS-Kurs aktiv die Map-Bearing treibt (schneller Fahrt),
   * ignorieren wir den Kompass: `_onPosition` ist dann die Bearing-Quelle
   * und der Kompass ist nur noch "stiller Listener" (läuft weiter, damit
   * der Bus nicht detached und bei Verlangsamen nahtlos übernehmen kann).
   *
   * Bei Stand oder unter EXIT_SPEED treibt der Kompass — genau was der
   * User wollte ("im Stand Handy drehen um den Berg zu suchen, Karte soll
   * mit"): `_gpsActive === false`, also fällt der if-Guard hier weg.
   */
  private _onHeading(heading: number): void {
    if (this._gpsActive) return;
    this._maybeApplyBearing(heading);
  }

  /**
   * Callback für den positionBus — GPS spricht.
   *
   * Hysterese für die Quellen-Wahl (damit wir am Übergangs-Speed nicht
   * zappeln):
   *   - Enter GPS: speed >= 2.5 m/s  UND  accuracy <= 25 m  UND  heading != null
   *   - Exit  GPS: speed  < 1.5 m/s  ODER heading == null
   *   - Dazwischen: bleibt bei der aktuellen Quelle
   *
   * heading auf coords ist Course over Ground (0..360, im Uhrzeigersinn
   * von Nord) — genau das Schema, das wir an map.setBearing() geben können.
   * Das OS liefert ihn erst bei genügend Bewegung; ist er null und wir
   * sind GPS-aktiv → Exit.
   */
  private _onPosition(coords: GeolocationCoordinates | null): void {
    if (!coords) {
      // Kein Fix → was immer wir vorher hatten, GPS kann's nicht mehr.
      // Wechsel zurück auf Kompass-Steuerung beim nächsten Heading-Event.
      this._gpsActive = false;
      return;
    }
    const speed = coords.speed ?? 0;
    const accuracy = coords.accuracy ?? Infinity;
    const cog = coords.heading;

    if (this._gpsActive) {
      // Bleiben wir drin oder raus?
      if (speed < GPS_SOURCE_EXIT_SPEED || cog == null || Number.isNaN(cog)) {
        this._gpsActive = false;
        return;
      }
      // Drin → COG als Map-Bearing.
      this._maybeApplyBearing(cog);
      return;
    }

    // Aktuell Kompass-gesteuert — rüber nach GPS?
    if (
      speed >= GPS_SOURCE_ENTER_SPEED &&
      accuracy <= GPS_SOURCE_MAX_ACCURACY_M &&
      cog != null &&
      !Number.isNaN(cog)
    ) {
      this._gpsActive = true;
      this._maybeApplyBearing(cog);
    }
    // sonst: bleibt Kompass. Kein setBearing hier; der nächste Compass-Tick
    // treibt die Drehung ganz normal weiter.
  }

  /**
   * Zentrale Anwendung einer Bearing auf die Map — mit Dead-Zone (korrekte
   * Kreis-Distanz via angleDelta, wrappt um 0°/360°) und 15-Hz-Throttle.
   *
   * Reihenfolge: Dead-Zone ZUERST, Throttle danach. Begründung (Review Wave
   * 14ax): die Dead-Zone killt Events komplett — ein Sub-Dead-Zone-Wobble
   * löst nie ein Rendering aus. Der Throttle ist nur ein Rate-Limiter gegen
   * 60-Hz-Burst über der Dead-Zone. Dead-Zone-first spart damit in den
   * häufigsten Fällen (still stehen, gerade fahren) die performance.now()-
   * Messung. Effekt ist minimal, aber die Semantik ist sauberer.
   *
   * Das `_lastAppliedBearing` wird NUR aktualisiert, wenn wir auch wirklich
   * setBearing() gerufen haben — sonst akkumuliert sich ein langsamer Drift
   * in der Dead-Zone zu einem sichtbaren Sprung.
   */
  private _maybeApplyBearing(heading: number): void {
    if (
      this._lastAppliedBearing != null &&
      angleDelta(heading, this._lastAppliedBearing) < HEADING_DEAD_ZONE_DEG
    ) {
      return;
    }
    const now = performance.now();
    if (now - this._lastApplyTime < HEADING_MIN_INTERVAL_MS) return;
    this._lastApplyTime = now;
    this._lastAppliedBearing = heading;
    this._map?.setBearing(heading);
  }

  private _update = (): void => {
    const map = this._map;
    if (!map || !this._rose) return;
    // In free-Modus: KEINE Counter-Rotation des Rose-Containers. Sonst würde
    // der Rotations-Arc (UI-Indikator, soll ruhig stehen) mit dem Map-Bearing
    // herumtanzen — verwirrend. Der graue Stern drin hat ohnehin kein rotes N,
    // also geht die "zeige mir Norden"-Info akzeptabel verloren solange User
    // frei rotiert.
    if (this._mode === 'free') {
      this._rose.style.transform = '';
      return;
    }
    // Bearing ist "Karte nach rechts gedreht" im Uhrzeigersinn. Wir wollen
    // dass unser rotes N echten Norden zeigt — also gegenrotieren.
    const b = map.getBearing();
    this._rose.style.transform = `rotate(${-b}deg)`;
  };
}

// Wave 14am: `requestOrientationPermission` entfernt — ersetzt durch
// `deviceHeadingBus.requestPermissionIfNeeded()`, damit CompassRose und
// LocateControl sich denselben Permission-Flow teilen.
